import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiEdit2, FiX, FiCheck } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import './DateBlockManager.css';

const DateBlockManager = ({ propertyId, apiEndpoint, onUpdate }) => {
  const { t } = useTranslation();
  const [dateBlocks, setDateBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: 'maintenance',
    notes: ''
  });

  useEffect(() => {
    fetchDateBlocks();
  }, [propertyId]);

  const fetchDateBlocks = async () => {
    try {
      const response = await api.get(`${apiEndpoint}/properties/${propertyId}/date-blocks`);
      setDateBlocks(response.data.date_blocks || []);
    } catch (error) {
      console.error('Error fetching date blocks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingBlock) {
        await api.put(`${apiEndpoint}/properties/${propertyId}/date-blocks/${editingBlock.id}`, formData);
        alert(t('dateBlock.blockUpdatedSuccess'));
      } else {
        await api.post(`${apiEndpoint}/properties/${propertyId}/date-blocks`, formData);
        alert(t('dateBlock.blockCreatedSuccess'));
      }

      setShowForm(false);
      setEditingBlock(null);
      setFormData({ start_date: '', end_date: '', reason: 'maintenance', notes: '' });
      fetchDateBlocks();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving date block:', error);
      alert(error.response?.data?.error || 'Erro ao salvar bloqueio');
    }
  };

  const handleDelete = async (blockId) => {
    if (!window.confirm(t('dateBlock.confirmRemoveBlock'))) return;

    try {
      await api.delete(`${apiEndpoint}/properties/${propertyId}/date-blocks/${blockId}`);
      alert(t('dateBlock.blockRemovedSuccess'));
      fetchDateBlocks();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting date block:', error);
      alert(t('dateBlock.errorRemovingBlock'));
    }
  };

  const handleEdit = (block) => {
    setEditingBlock(block);
    setFormData({
      start_date: block.start_date,
      end_date: block.end_date,
      reason: block.reason || 'maintenance',
      notes: block.notes || ''
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingBlock(null);
    setFormData({ start_date: '', end_date: '', reason: 'maintenance', notes: '' });
  };

  const getReasonLabel = (reason) => {
    const reasons = {
      maintenance: 'Manutenção',
      personal_use: 'Uso Pessoal',
      renovation: 'Renovação',
      cleaning: 'Limpeza',
      other: 'Outro'
    };
    return reasons[reason] || reason;
  };

  const getReasonColor = (reason) => {
    const colors = {
      maintenance: '#ffc107',
      personal_use: '#007bff',
      renovation: '#dc3545',
      cleaning: '#28a745',
      other: '#6c757d'
    };
    return colors[reason] || '#6c757d';
  };

  const isPast = (dateString) => {
    return new Date(dateString) < new Date();
  };

  if (loading) {
    return <div className="date-blocks-loading">Carregando bloqueios...</div>;
  }

  return (
    <div className="date-block-manager">
      <div className="manager-header">
        <h3>Bloqueios de Datas</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary btn-sm"
        >
          <FiPlus /> Novo Bloqueio
        </button>
      </div>

      {showForm && (
        <div className="date-block-form-container">
          <form onSubmit={handleSubmit} className="date-block-form">
            <div className="form-header">
              <h4>{editingBlock ? 'Editar Bloqueio' : 'Novo Bloqueio'}</h4>
              <button type="button" onClick={handleCancel} className="btn-close">
                <FiX />
              </button>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Data Início *</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="form-input"
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Data Fim *</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="form-input"
                  required
                  min={formData.start_date || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Motivo *</label>
              <select
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="form-input"
                required
              >
                <option value="maintenance">Manutenção</option>
                <option value="personal_use">Uso Pessoal</option>
                <option value="renovation">Renovação</option>
                <option value="cleaning">Limpeza</option>
                <option value="other">Outro</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{t('dateBlock.notes')}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="form-input"
                rows="3"
                placeholder={t('dateBlock.notesPlaceholder')}
              />
            </div>

            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary">
                <FiCheck /> {editingBlock ? 'Atualizar' : 'Criar Bloqueio'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="date-blocks-list">
        {dateBlocks.length === 0 ? (
          <div className="empty-state">
            <p>Nenhum bloqueio de datas configurado.</p>
            <p className="text-muted">Bloqueie datas para manutenção, uso pessoal ou outros motivos.</p>
          </div>
        ) : (
          <div className="blocks-grid">
            {dateBlocks.map(block => {
              const past = isPast(block.end_date);
              return (
                <div key={block.id} className={`block-card ${past ? 'past' : ''}`}>
                  <div className="block-header">
                    <span
                      className="block-reason-badge"
                      style={{ backgroundColor: getReasonColor(block.reason) }}
                    >
                      {getReasonLabel(block.reason)}
                    </span>
                    {past && <span className="past-badge">Passado</span>}
                  </div>

                  <div className="block-dates">
                    <div className="date-item">
                      <span className="date-label">Início:</span>
                      <span className="date-value">
                        {new Date(block.start_date).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <div className="date-item">
                      <span className="date-label">Fim:</span>
                      <span className="date-value">
                        {new Date(block.end_date).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>

                  {block.notes && (
                    <div className="block-notes">
                      <p>{block.notes}</p>
                    </div>
                  )}

                  <div className="block-actions">
                    <button
                      onClick={() => handleEdit(block)}
                      className="btn-icon"
                      title="Editar"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      onClick={() => handleDelete(block.id)}
                      className="btn-icon btn-danger"
                      title="Remover"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DateBlockManager;
